import { useRef, useState } from 'react';
import { referralContent } from '../../content/referral';
import { track } from '../../lib/analytics';
import type { SignupAudience } from '../../lib/signupSession';

/**
 * The share option that follows a referral (spec sections 10 and 17).
 *
 * **The site sends nothing.** There is no send button, no mail link to the
 * person who was recommended, and no field to type an address into. There is a
 * block of text the referrer can read in full and a button that puts it on
 * their clipboard. They send it themselves, from their own phone, in their own
 * words if they want to change it — which is the whole point of section 17.
 *
 * The message is in a real, selectable, read-only `<textarea>` rather than a
 * styled `<p>`, so the fallback is always available: a browser that blocks
 * clipboard access still leaves someone a control they can select from with a
 * keyboard or a long press. `onCopy` on that control records the event for a
 * manual copy the same way the button does.
 *
 * The link is to joinherdbook.com, which 301s to /providers with the query
 * string intact — a shared message is provider outreach, and its attribution
 * has to survive the redirect like every other campaign link (CLAUDE.md).
 */

export interface SharePromptProps {
  audience: SignupAudience;
  /** Ids are derived from this so a page could render more than one. */
  idPrefix: string;
}

type CopyStatus = 'idle' | 'copied' | 'manual';

export function SharePrompt({ audience, idPrefix }: SharePromptProps) {
  const copy = referralContent[audience].share;
  const shared = referralContent.common.share;

  const [status, setStatus] = useState<CopyStatus>('idle');
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  const headingId = `${idPrefix}-share-heading`;
  const messageId = `${idPrefix}-share-message`;

  const noteCopied = () => {
    setStatus('copied');
    track('share_link_copied', { referrer: audience });
  };

  /** Nothing was copied, so nothing is recorded as copied. Put the text under
   * the cursor instead and let them take it themselves. */
  const offerManualCopy = () => {
    const field = messageRef.current;
    field?.focus();
    field?.select();
    setStatus('manual');
  };

  const handleCopy = () => {
    void (async () => {
      // The API is absent over plain HTTP and in older browsers, and
      // present-but-refused in some privacy configurations. Both end the same
      // way, and neither may be mistaken for a successful copy — `await` on the
      // `undefined` an absent API returns would resolve happily.
      const clipboard = navigator.clipboard;
      if (typeof clipboard?.writeText === 'function') {
        try {
          await clipboard.writeText(copy.message);
          noteCopied();
          return;
        } catch {
          // Refused. Fall through.
        }
      }
      offerManualCopy();
    })();
  };

  const statusMessage =
    status === 'copied' ? shared.copiedStatus : status === 'manual' ? shared.manualCopyStatus : '';

  return (
    <div className="mt-8 border-t-2 border-utility pt-6">
      <h3 id={headingId} className="text-lg font-semibold text-ink">
        {copy.headline}
      </h3>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/85">{copy.body}</p>

      <div className="mt-5 max-w-2xl">
        <label htmlFor={messageId} className="block text-sm font-semibold text-ink">
          {shared.messageLabel}
        </label>
        <textarea
          id={messageId}
          ref={messageRef}
          readOnly
          rows={5}
          value={copy.message}
          onCopy={noteCopied}
          className="mt-2 block min-h-32 w-full resize-y rounded-md border-2 border-ink/30 bg-field px-4 py-3 text-base leading-relaxed text-ink focus:border-ink focus:outline-2 focus:outline-offset-2 focus:outline-ink"
        />
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex min-h-12 w-full items-center justify-center rounded-md border-2 border-ink px-6 py-3 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-field focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:w-auto"
        >
          {shared.copyLabel}
        </button>
        {/* Polite, not assertive: the confirmation is useful, not urgent. */}
        <p aria-live="polite" className="mt-3 min-h-6 text-sm leading-relaxed text-ink/85">
          {statusMessage}
        </p>
      </div>
    </div>
  );
}
